import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LocationsSection() {
  const [locations, setLocations] = useState([]);
  const navigate = useNavigate();
  const scrollRef = useRef(null);

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await axios.get(`${API}/locations`);
        setLocations(response.data);
      } catch (error) {
        console.error('Error fetching locations:', error);
      }
    };
    fetchLocations();
  }, []);

  const handleLocationClick = (location) => {
    navigate(`/find-space?location=${location.area}`);
  };

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = direction === 'left' ? -400 : 400;
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  return (
    <section className="section-padding bg-white" data-testid="locations-section">
      <div className="container-wide mx-auto px-4">
        {/* Heading with underline */}
        <div className="mb-12">
          <h2 className="text-4xl sm:text-5xl font-bold text-[#222] mb-2">Our Locations</h2>
          <div className="w-24 h-1 bg-[#E12429]"></div>
        </div>

        {/* Slider */}
        <div className="relative">
          {/* Navigation Buttons */}
          {locations.length > 3 && (
            <>
              <button
                onClick={() => scroll('left')}
                className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-lg hover:bg-gray-100 transition-all"
              >
                <ChevronLeft className="text-[#222]" size={24} />
              </button>
              <button
                onClick={() => scroll('right')}
                className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-lg hover:bg-gray-100 transition-all"
              >
                <ChevronRight className="text-[#222]" size={24} />
              </button>
            </>
          )}

          {/* Location Cards Slider */}
          <div
            ref={scrollRef}
            className="flex overflow-x-auto space-x-6 pb-4 hide-scrollbar scroll-smooth"
            style={{ scrollSnapType: 'x mandatory' }}
          >
            {locations.map((location, index) => (
              <div
                key={location.id}
                className="flex-shrink-0 w-full md:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] cursor-pointer group"
                style={{ scrollSnapAlign: 'start' }}
                onClick={() => handleLocationClick(location)}
                data-testid={`location-card-${index}`}
              >
                <div className="relative h-96 rounded-lg overflow-hidden shadow-lg transition-all duration-300 group-hover:shadow-2xl group-hover:-translate-y-2">
                  <img
                    src={location.image}
                    alt={location.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                  
                  {/* Location Name Overlay */}
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <h3 className="text-3xl font-bold text-white mb-1">{location.name}</h3>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}